// Scheduled-routine helpers (HERMES B1) shared by the Routines tab.
// Kept out of hosted-agents.ts to stay under the module size cap; the form
// state in hosted-agents.ts imports the type and the parse/serialize helpers.

import type {
  HostedAgentRoutine,
  HostedAgentRoutineDelivery,
} from "@/api/admin/hosted-agents-routines"

/** Tools requiring an explicitly enabled background Microsoft connection. */
export const HOSTED_AGENT_DELEGATED_TOKEN_TOOLS = new Set(["microsoft_365_search"])

export const HOSTED_AGENT_ROUTINE_NAME_MAX_LENGTH = 120
export const HOSTED_AGENT_ROUTINE_PROMPT_MAX_LENGTH = 4000
export const HOSTED_AGENT_ROUTINE_MIN_INTERVAL_MINUTES = 1

/**
 * A single scheduled routine as the wizard edits it. Exactly one of `cron`
 * (5-field crontab) / `intervalMinutes` (>= 1) carries the cadence; the other
 * is null. Server-managed timestamps are preserved verbatim.
 */
export type HostedAgentRoutineFormState = {
  id: string
  name: string
  enabled: boolean
  cron: string | null
  intervalMinutes: number | null
  timezone: string
  prompt: string
  createdAt: string | null
  updatedAt: string | null
  createdBy: string | null
  useOwnerM365Connection: boolean
  delivery: HostedAgentRoutineDelivery
  hasDeliveryPolicy: boolean
}

/** The agent tools selected that cannot run in a scheduled routine. */
export function hostedAgentDelegatedToolsSelected(tools: readonly string[]): string[] {
  return tools.filter((tool) => HOSTED_AGENT_DELEGATED_TOKEN_TOOLS.has(tool))
}

function createHostedAgentRoutineId(): string {
  const random =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2)
  return `routine:${random}`
}

/** Parse the stored `routines[]` (unknown[]) into editable form entries. */
export function parseHostedAgentRoutines(routines: unknown[]): HostedAgentRoutineFormState[] {
  if (!Array.isArray(routines)) return []
  return routines
    .filter(
      (entry): entry is Record<string, unknown> => typeof entry === "object" && entry !== null
    )
    .map((entry) => {
      const cron = typeof entry.cron === "string" && entry.cron.trim() ? entry.cron.trim() : null
      const rawInterval = entry.interval_minutes
      const intervalMinutes =
        typeof rawInterval === "number" && Number.isFinite(rawInterval) && rawInterval >= 1
          ? Math.floor(rawInterval)
          : null
      return {
        id: typeof entry.id === "string" && entry.id ? entry.id : createHostedAgentRoutineId(),
        name: typeof entry.name === "string" ? entry.name : "",
        enabled: entry.enabled !== false,
        // Cron wins if both are present (defensive; the model forbids it).
        cron,
        intervalMinutes: cron ? null : intervalMinutes,
        timezone: typeof entry.timezone === "string" && entry.timezone ? entry.timezone : "UTC",
        prompt: typeof entry.prompt === "string" ? entry.prompt : "",
        createdAt: typeof entry.created_at === "string" ? entry.created_at : null,
        updatedAt: typeof entry.updated_at === "string" ? entry.updated_at : null,
        createdBy: typeof entry.created_by === "string" ? entry.created_by : null,
        useOwnerM365Connection: entry.use_owner_m365_connection === true,
        delivery: parseRoutineDelivery(entry.delivery),
        hasDeliveryPolicy: entry.delivery !== null && typeof entry.delivery === "object",
      }
    })
}

/** Serialize a form routine back into the stored `Routine` document shape. */
export function toHostedAgentRoutineDoc(routine: HostedAgentRoutineFormState): HostedAgentRoutine {
  const usesInterval = routine.cron === null && routine.intervalMinutes !== null
  return {
    id: routine.id,
    name: routine.name.trim(),
    enabled: routine.enabled,
    trigger: "schedule",
    cron: usesInterval ? null : routine.cron?.trim() || null,
    interval_minutes: usesInterval ? routine.intervalMinutes : null,
    timezone: routine.timezone || "UTC",
    prompt: routine.prompt,
    use_owner_m365_connection: routine.useOwnerM365Connection,
    ...(routine.hasDeliveryPolicy ? { delivery: routine.delivery } : {}),
    ...(routine.createdAt ? { created_at: routine.createdAt } : {}),
    ...(routine.updatedAt ? { updated_at: routine.updatedAt } : {}),
    ...(routine.createdBy ? { created_by: routine.createdBy } : {}),
  }
}

/** A fresh, enabled daily routine with a stable client id. */
export function createBlankHostedAgentRoutine(): HostedAgentRoutineFormState {
  return {
    id: createHostedAgentRoutineId(),
    name: "",
    enabled: true,
    cron: "0 8 * * *",
    intervalMinutes: null,
    timezone: "UTC",
    prompt: "",
    createdAt: null,
    updatedAt: null,
    createdBy: null,
    useOwnerM365Connection: false,
    delivery: parseRoutineDelivery(null),
    hasDeliveryPolicy: true,
  }
}

export type HostedAgentRoutineFrequency = "interval" | "daily" | "weekly" | "advanced"

/** The friendly editor state derived from a routine's stored cadence. */
export type HostedAgentRoutineScheduleForm = {
  frequency: HostedAgentRoutineFrequency
  intervalMinutes: number
  time: string
  weekday: string
  cron: string
}

const CRON_TIME_RE = /^(\d{1,2})\s+(\d{1,2})\s+\*\s+\*\s+(\*|[0-6])$/

/** Infer the friendly schedule editor state from a routine's stored cadence. */
export function readHostedAgentRoutineSchedule(
  routine: HostedAgentRoutineFormState
): HostedAgentRoutineScheduleForm {
  const base: HostedAgentRoutineScheduleForm = {
    frequency: "daily",
    intervalMinutes: 60,
    time: "08:00",
    weekday: "1",
    cron: routine.cron ?? "0 8 * * *",
  }
  if (routine.cron === null && routine.intervalMinutes !== null) {
    return { ...base, frequency: "interval", intervalMinutes: routine.intervalMinutes }
  }
  const cron = routine.cron?.trim() ?? ""
  const match = CRON_TIME_RE.exec(cron)
  if (!match) return { ...base, frequency: "advanced", cron }
  const minute = Number(match[1])
  const hour = Number(match[2])
  const weekday = match[3]
  const time = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`
  if (weekday === "*") return { ...base, frequency: "daily", time, cron }
  return { ...base, frequency: "weekly", time, weekday, cron }
}

function parseRoutineTime(time: string): { hour: number; minute: number } | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(time.trim())
  if (!match) return null
  const hour = Number(match[1])
  const minute = Number(match[2])
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null
  return { hour, minute }
}

/** Map the friendly editor state back onto a routine's cron/interval fields. */
export function applyHostedAgentRoutineSchedule(
  routine: HostedAgentRoutineFormState,
  schedule: HostedAgentRoutineScheduleForm
): HostedAgentRoutineFormState {
  if (schedule.frequency === "interval") {
    return {
      ...routine,
      cron: null,
      intervalMinutes: Math.max(1, Math.floor(schedule.intervalMinutes)),
    }
  }
  if (schedule.frequency === "advanced") {
    return { ...routine, cron: schedule.cron.trim(), intervalMinutes: null }
  }
  const parsed = parseRoutineTime(schedule.time)
  if (!parsed) return { ...routine, cron: "", intervalMinutes: null }
  const cron =
    schedule.frequency === "weekly"
      ? `${parsed.minute} ${parsed.hour} * * ${schedule.weekday}`
      : `${parsed.minute} ${parsed.hour} * * *`
  return { ...routine, cron, intervalMinutes: null }
}

export type HostedAgentRoutineFieldError = "name" | "schedule" | "prompt"

/** Validate one routine; empty result means it is savable. */
export function validateHostedAgentRoutine(
  routine: HostedAgentRoutineFormState
): HostedAgentRoutineFieldError[] {
  const errors: HostedAgentRoutineFieldError[] = []
  if (!routine.name.trim()) errors.push("name")
  if (!routine.prompt.trim()) errors.push("prompt")
  const hasCron = routine.cron !== null && routine.cron.trim().length > 0
  const hasInterval =
    routine.intervalMinutes !== null &&
    Number.isInteger(routine.intervalMinutes) &&
    routine.intervalMinutes >= HOSTED_AGENT_ROUTINE_MIN_INTERVAL_MINUTES
  if (hasCron === hasInterval) errors.push("schedule")
  return errors
}

/** A localization-ready descriptor of a routine's cadence for summary text. */
export type HostedAgentRoutineScheduleSummary =
  | { kind: "interval"; minutes: number }
  | { kind: "daily"; time: string }
  | { kind: "weekly"; weekday: string; time: string }
  | { kind: "cron"; cron: string }
  | { kind: "unset" }

export function describeHostedAgentRoutineSchedule(
  routine: HostedAgentRoutineFormState
): HostedAgentRoutineScheduleSummary {
  if (routine.cron === null && routine.intervalMinutes !== null) {
    return { kind: "interval", minutes: routine.intervalMinutes }
  }
  const schedule = readHostedAgentRoutineSchedule(routine)
  if (schedule.frequency === "daily") return { kind: "daily", time: schedule.time }
  if (schedule.frequency === "weekly") {
    return { kind: "weekly", weekday: schedule.weekday, time: schedule.time }
  }
  const cron = routine.cron?.trim()
  return cron ? { kind: "cron", cron } : { kind: "unset" }
}

export const HOSTED_AGENT_WEEKDAY_KEYS = ["0", "1", "2", "3", "4", "5", "6"] as const

export const HOSTED_AGENT_WEEKDAY_DEFAULTS: Record<string, string> = {
  "0": "Sunday",
  "1": "Monday",
  "2": "Tuesday",
  "3": "Wednesday",
  "4": "Thursday",
  "5": "Friday",
  "6": "Saturday",
}

export const HOSTED_AGENT_RUN_STATUS_DEFAULTS: Record<string, string> = {
  queued: "Queued",
  running: "Running",
  succeeded: "Succeeded",
  failed: "Failed",
  cancelled: "Cancelled",
}

function parseRoutineDelivery(value: unknown): HostedAgentRoutineDelivery {
  const doc = value && typeof value === "object" ? (value as Record<string, unknown>) : {}
  const strings = (items: unknown) =>
    Array.isArray(items) ? items.filter((item): item is string => typeof item === "string") : []
  return {
    channels: strings(doc.channels).filter(
      (channel): channel is "email" | "teams" => channel === "email" || channel === "teams"
    ),
    email_recipients: strings(doc.email_recipients),
    teams_destination_ids: strings(doc.teams_destination_ids),
    include_content: doc.include_content === true,
    audience: doc.audience === "agent_audience" ? "agent_audience" : "private",
  }
}
